/**
 * Releases — DE-171 full update-bundle delivery operator UI.
 *
 * A `release` bundles update parts and is dispatched as ONE `release` IoT Job to
 * a single device or an AWS IoT thing-group. This page is the operator surface:
 *
 *   • Compose + deploy — pick an OS artifact, a content profile, an app artifact,
 *     and/or a uC-fw artifact, pick a target (device or thing-group), preview the
 *     composition, and dispatch. The meaningful-combination rule is enforced
 *     client-side (mirrors the 400 the backend would return): an OS part may only
 *     be accompanied by content (app + ucfw ship inside the OS image); with no OS,
 *     at least one of {app, content, ucfw} is required.
 *   • Artifact registries — read-only os/app/fw lists so the operator sees what's
 *     deployable. Registries are populated by the scripts/upload-{os,app,fw}.sh
 *     CLIs, not the Portal.
 *   • Deployments ledger — the CURRENT release per target (target, label, job, when).
 *
 * Super-admin only (the backend gates writes with requireRole; the registries +
 * ledger reads are tenant-scoped via RLS). Mirrors the Content page's
 * table/modal/toast/error patterns; reuses DevicePicker for the device target.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useContentProfiles,
  useDeployRelease,
  useNextReleaseId,
  usePublishedReleases,
  useUpdateReleaseNotes,
  useDeviceGroups,
  useDevices,
  useMe,
  useArtifactUploadUrl,
  useRegisterArtifact,
  useReleaseArtifacts,
  useReleaseCapability,
  useReleaseDeployments,
  useReleaseJobMembers,
  useReleaseStatuses,
  useSimBatch,
  ARTOMEUI_ENV_GROUPS,
  ENV_GROUP_LABEL,
  type ArtifactType,
  type CapabilityPreview,
  type ContentProfile,
  type DeviceSimStatus,
  type EnvGroup,
  type ReleaseArtifact,
  type ReleaseDeployment,
  type PublishedRelease,
  type ReleaseDeployParts,
  type ReleaseJobMember,
  type ReleaseJobStatus,
  type SimQuotaSummary, useHardwareTypes,} from '../api/hooks';
import { ApiError } from '../api/client';
import { explainInvalidReleaseId } from '../lib/releaseId';
import {
  bucketQuota,
  deployPartsFromLedger,
  describeEstimate,
  estimateReleaseBytes,
  type QuotaBuckets,
  type ReleaseSizeEstimate,
} from '../lib/simQuota';
import { DevicePicker } from '../components/DevicePicker';
import { SimTopUpDialog } from '../components/SimTopUpDialog';
import { Skeleton } from '../components/Skeleton';
import {
  jobPill,
  releaseOutcomePill,
  memberOutcomeBucket,
  type OutcomeBucket,
} from '../components/contentSync';
import { fmtAgo, fmtBytes } from '../components/format';
import { putToS3 } from '../components/s3upload';
import { useToast } from '../components/Toast';
import {
  bucketByHardware, evaluateCoverage, evaluateDeploy, releaseCoverage,
  type CoverageVerdict, type DeployVerdict,
} from '../lib/hardware';

// Releases target the env thing-groups (unassigned is a landing zone, never a
// deploy target — same convention as the content deploy).
const DEPLOY_GROUPS = ARTOMEUI_ENV_GROUPS.filter((g) => g !== 'artomeui-unassigned');

type TargetKind = 'device' | 'thing-group';

// Which selected parts we'll actually send (an OS deploy drops any app/ucfw the
// operator may have left selected, matching the rule).
interface PartSelection {
  osId: string;
  contentProfileId: string;
  appId: string;
  ucfwId: string;
}

export function Releases() {
  const me = useMe();

  if (me.data && me.data.role !== 'super-admin') {
    return (
      <div className="card max-w-md">
        <h2 className="font-semibold mb-1">Forbidden</h2>
        <p className="text-sm text-muted">Releases are composed and deployed by super-admins.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Releases</h1>
          <p className="text-sm text-muted">
            Compose an update bundle (OS, content, app, uC firmware) and deploy it to a device or a
            device group as a single update job.
          </p>
        </div>
        <Link
          to="/admin/content"
          className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft"
        >
          Content library →
        </Link>
      </div>

      <ComposeSection />
      <PublishedReleasesSection />
      <RegistriesSection />
      <DeploymentsSection />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// DE-223 — Published releases as first-class entities.
//
// A release used to be a one-shot: compose parts, dispatch, and the only trace
// was a per-target ledger row. Now each published release ID is a stored entity
// carrying the FROZEN part set it names, so it can be re-applied to another
// device or group later without recomposing it by hand — the backend resolves
// the parts from the registry, so a re-deploy is the same bundle by construction.
//
// The note is the only editable field. Everything identifying the release stays
// immutable, because devices, release notes and the Jira version key off it.
// ─────────────────────────────────────────────────────────────────────────
function PublishedReleasesSection() {
  const published = usePublishedReleases();
  const [redeploy, setRedeploy] = useState<PublishedRelease | null>(null);
  const releases = published.data?.releases ?? [];

  return (
    <section className="card space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Published releases</h2>
          <p className="text-xs text-muted">
            Each release ID names a frozen set of parts. Re-apply one to another target without
            recomposing it — the exact same bundle is re-sent.
          </p>
        </div>
      </div>

      {published.isLoading && (
        <div className="space-y-2">
          <Skeleton.Line /><Skeleton.Line /><Skeleton.Line />
        </div>
      )}
      {published.error && (
        <p className="text-sm text-warn">
          Failed to load releases: {(published.error as Error).message}
        </p>
      )}
      {published.data && releases.length === 0 && (
        <div className="text-center py-8 space-y-1">
          <p className="text-sm font-medium">No published releases yet</p>
          <p className="text-xs text-muted">
            Deploy with a Release ID set above and it becomes a reusable release here.
          </p>
        </div>
      )}

      {releases.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted border-b border-border">
              <th className="pb-1.5 pr-4 font-medium">Release</th>
              <th className="pb-1.5 pr-4 font-medium">Parts</th>
              <th className="pb-1.5 pr-4 font-medium">Published</th>
              <th className="pb-1.5 pr-4 font-medium">Note</th>
              <th className="pb-1.5" />
            </tr>
          </thead>
          <tbody>
            {releases.map((r) => (
              <PublishedReleaseRow key={r.release_id} release={r} onRedeploy={setRedeploy} />
            ))}
          </tbody>
        </table>
      )}

      {redeploy && (
        <RedeployModal release={redeploy} onClose={() => setRedeploy(null)} />
      )}
    </section>
  );
}

/** One stored release: its parts, provenance, and an inline-editable note. */
function PublishedReleaseRow({
  release, onRedeploy,
}: { release: PublishedRelease; onRedeploy: (r: PublishedRelease) => void }) {
  const toast = useToast();
  const update = useUpdateReleaseNotes();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(release.notes ?? '');

  const save = async () => {
    try {
      await update.mutateAsync({ releaseId: release.release_id, notes: draft.trim() || null });
      setEditing(false);
      toast.success(`Note saved for ${release.release_id}.`);
    } catch (e) {
      toast.error(`Could not save the note: ${(e as Error).message}`);
    }
  };

  const partSummary = Object.entries(release.parts ?? {})
    .map(([kind, v]) => {
      const p = v as { version?: string; profileName?: string } | undefined;
      if (kind === 'content') return `content:${p?.profileName ?? '?'}-${p?.version ?? '?'}`;
      return `${kind}:${p?.version ?? '?'}`;
    })
    .join(' · ');

  return (
    <tr className="border-b border-border last:border-0 align-top">
      <td className="py-2 pr-4">
        <span className="font-mono font-semibold">{release.release_id}</span>
        {release.build_number != null && (
          <span className="block text-muted">build {release.build_number}</span>
        )}
      </td>
      <td className="py-2 pr-4 font-mono text-[11px] max-w-[16rem]">
        {partSummary || <span className="text-muted">—</span>}
        {release.git_commit && (
          <span className="block text-muted">commit {release.git_commit}</span>
        )}
      </td>
      <td className="py-2 pr-4 whitespace-nowrap">
        {new Date(release.published_at).toLocaleDateString()}
      </td>
      <td className="py-2 pr-4 max-w-[22rem]">
        {editing ? (
          <div className="space-y-1">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              maxLength={2000}
              aria-label={`Note for release ${release.release_id}`}
              className="w-full px-2 py-1 border border-border rounded-md text-xs"
              placeholder="What is this release for? Where did it go?"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void save()}
                disabled={update.isPending}
                className="px-2 py-0.5 bg-accent text-white rounded-md font-medium disabled:opacity-50"
              >
                {update.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => { setDraft(release.notes ?? ''); setEditing(false); }}
                className="px-2 py-0.5 border border-border rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-left w-full hover:bg-soft rounded px-1 -mx-1"
            title="Click to edit this note"
          >
            {release.notes ?? <span className="text-muted italic">Add a note…</span>}
          </button>
        )}
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={() => onRedeploy(release)}
          className="px-2 py-1 border border-border rounded-md font-medium hover:bg-soft"
          title="Send this exact release to another device or group"
        >
          Deploy again
        </button>
      </td>
    </tr>
  );
}

/**
 * Re-apply a stored release. Deliberately sends ONLY the releaseId and the
 * target: the backend resolves the frozen part set, so this cannot drift from
 * what was originally published (and cannot trip the immutability 409).
 *
 * Sending no parts does NOT mean the bundle can't be sized. `release.parts` is
 * the ledger shape, which carries the very artifact/profile ids the backend will
 * re-resolve — so deployPartsFromLedger() hands the estimator exactly what will
 * ship, and this modal runs the same pre-flight as Compose & deploy: the SIM
 * quota check over the target set, and the group-scoped capability preview.
 * Both ack-gate the button; neither hard-blocks.
 */
function RedeployModal({
  release, onClose,
}: { release: PublishedRelease; onClose: () => void }) {
  const toast = useToast();
  const deploy = useDeployRelease();
  const devices = useDevices();
  const deviceGroups = useDeviceGroups();
  // Size sources for the frozen parts. Already mounted by ComposeSection on this
  // same page, so these resolve from the react-query cache without a spinner.
  const osArtifacts = useReleaseArtifacts('os');
  const appArtifacts = useReleaseArtifacts('app');
  const fwArtifacts = useReleaseArtifacts('fw');
  const profiles = useContentProfiles();
  const [targetKind, setTargetKind] = useState<TargetKind>('thing-group');
  const [group, setGroup] = useState<EnvGroup>(DEPLOY_GROUPS[0]!);
  const [things, setThings] = useState<Set<string>>(new Set());
  const [force, setForce] = useState(false);
  const [ackQuota, setAckQuota] = useState(false);
  const [ackStageOnly, setAckStageOnly] = useState(false);

  const groupOf = useMemo(() => {
    const m = new Map<string, EnvGroup>();
    for (const g of deviceGroups.data?.groups ?? []) for (const t of g.devices) m.set(t, g.name);
    return m;
  }, [deviceGroups.data]);

  const targetCount = targetKind === 'thing-group' ? 1 : things.size;

  // The group's CURRENT members — the capability preview needs them (the in-VPC
  // backend can't resolve thing-group membership) and the quota check buckets
  // the same set, so derive once and share.
  const capMembers = useMemo(
    () =>
      targetKind === 'thing-group'
        ? deviceGroups.data?.groups.find((g) => g.name === group)?.devices ?? []
        : [],
    [targetKind, group, deviceGroups.data],
  );
  const quotaThings = useMemo(
    () => (targetKind === 'thing-group' ? capMembers : [...things]),
    [targetKind, capMembers, things],
  );

  // DE-186 — capability preview. Group-only, exactly as on Compose & deploy:
  // /releases/capability takes ONE targetId, while this modal's device mode is a
  // multi-select. Picked devices instead get a definitive per-device 409 from
  // assertReleaseCapable, surfaced by describeDeployError.
  const hasOs = !!release.parts?.os;
  const capability = useReleaseCapability({
    targetKind,
    targetId: targetKind === 'thing-group' ? group : null,
    members: capMembers,
    force,
    hasOs,
    enabled: targetKind === 'thing-group' && capMembers.length > 0,
  });
  const preview = capability.data;

  // DE-310 — hardware guard. Resolved in the Portal because the two sources sit
  // on opposite sides of the VPC: the shadow-derived column comes from the
  // in-VPC backend, the thing attribute from the admin Lambda. The backend
  // keeps its own per-device 409 (DE-309) as defence in depth; this is the part
  // that can warn BEFORE anyone clicks.
  const hardwareTypesForGuard = useHardwareTypes();
  const osArtifactForGuard = release.parts?.os
    ? (osArtifacts.data?.artifacts ?? []).find((a) => a.id === release.parts!.os!.artifactId)
    : null;
  const hardwareVerdict = useMemo<DeployVerdict>(() => {
    if (targetKind !== 'thing-group' || capMembers.length === 0) return { kind: 'ok' };
    const reportedBy: Record<string, string | null> = {};
    for (const d of devices.data?.devices ?? []) reportedBy[d.thing] = d.device_type;
    const attributeBy =
      deviceGroups.data?.groups.find((g) => g.name === group)?.device_types ?? {};
    const buckets = bucketByHardware(
      capMembers, reportedBy, attributeBy, hardwareTypesForGuard.data?.hardwareTypes);
    return evaluateDeploy(osArtifactForGuard?.device_type, buckets);
  }, [targetKind, capMembers, devices.data, deviceGroups.data, group,
      hardwareTypesForGuard.data, osArtifactForGuard]);

  // DE-312 — does this release carry an image for the hardware actually in the
  // target? Distinct from DE-310's verdict, which asks whether the ONE selected
  // OS artifact matches. This asks whether the RELEASE — which may carry an
  // image per board — covers the group, and it treats a partial rollout as
  // legitimate-but-deliberate rather than as an error.
  const [ackCoverage, setAckCoverage] = useState(false);
  const coverageVerdict = useMemo<CoverageVerdict>(() => {
    if (targetKind !== 'thing-group' || capMembers.length === 0)
      return { kind: 'ok', covered: [] };
    const reportedBy: Record<string, string | null> = {};
    for (const d of devices.data?.devices ?? []) reportedBy[d.thing] = d.device_type;
    const attributeBy =
      deviceGroups.data?.groups.find((g) => g.name === group)?.device_types ?? {};
    const buckets = bucketByHardware(
      capMembers, reportedBy, attributeBy, hardwareTypesForGuard.data?.hardwareTypes);
    return evaluateCoverage(
      releaseCoverage(release.parts, osArtifactForGuard?.device_type), buckets);
  }, [targetKind, capMembers, devices.data, deviceGroups.data, group,
      hardwareTypesForGuard.data, release.parts, osArtifactForGuard]);
  const needsAck =
    targetKind === 'thing-group' &&
    !!preview &&
    preview.forcedNonOs &&
    (preview.buckets.stageOnly.length > 0 || preview.buckets.unsupported.length > 0);

  // DE-266 — SIM quota check over the frozen bundle. Covers BOTH target kinds,
  // since the SPA supplies the thing list either way.
  const simBatch = useSimBatch(quotaThings, quotaThings.length > 0);
  const frozenParts = useMemo(() => deployPartsFromLedger(release.parts), [release.parts]);
  const sizeEstimate = useMemo(
    () =>
      estimateReleaseBytes(
        frozenParts,
        osArtifacts.data?.artifacts ?? [],
        appArtifacts.data?.artifacts ?? [],
        fwArtifacts.data?.artifacts ?? [],
        profiles.data?.profiles ?? [],
      ),
    [frozenParts, osArtifacts.data, appArtifacts.data, fwArtifacts.data, profiles.data],
  );
  const quotaBuckets = useMemo(
    () => bucketQuota(quotaThings, simBatch.data?.sims, sizeEstimate),
    [quotaThings, simBatch.data, sizeEstimate],
  );
  const needsQuotaAck =
    quotaBuckets.blocked.length > 0 || quotaBuckets.insufficient.length > 0;

  // Re-arm both acks whenever the deploy shape changes — never carry a stale
  // tick. The release id is in the signature because the parent doesn't key this
  // modal, so it can swap the release prop in place; the bucket counts are there
  // because the ack copy NAMES them, and the 60 s SIM refetch can move a device
  // between buckets underneath an already-ticked box.
  const deployShapeSig =
    `${release.release_id}|${targetKind}|${group}|${force}|${things.size}`
    + `|${capMembers.length}|${quotaThings.length}`
    + `|${sizeEstimate.bytes}|${sizeEstimate.confidence}`
    + `|${quotaBuckets.blocked.length}:${quotaBuckets.insufficient.length}`
    + `|${preview?.buckets.stageOnly.length ?? 0}:${preview?.buckets.unsupported.length ?? 0}`;
  useEffect(() => {
    setAckQuota(false);
    setAckStageOnly(false);
  }, [deployShapeSig]);

  const canDeploy =
    targetCount > 0 && !deploy.isPending
    && (!needsQuotaAck || ackQuota) && (!needsAck || ackStageOnly)
    // DE-310 — a release that matches NO member cannot be dispatched. A mixed
    // group only warns: the non-matching units refuse it cheaply (DE-309), and
    // blocking a legitimate partial rollout would be worse than informing it.
    && hardwareVerdict.kind !== 'block'
    // DE-312 — a release covering NOTHING in the target updates nothing, so it
    // is refused. A PARTIAL rollout is legitimate — one board type first is a
    // normal thing to want — so it asks rather than blocks.
    && coverageVerdict.kind !== 'none'
    && (coverageVerdict.kind !== 'partial' || ackCoverage);

  const run = async () => {
    const targets =
      targetKind === 'thing-group'
        ? [{ targetKind: 'thing-group' as const, targetId: group }]
        : [...things].map((t) => ({ targetKind: 'device' as const, targetId: t }));
    const results = await Promise.allSettled(
      targets.map((t) =>
        // No `parts` — the backend resolves the frozen set from the registry.
        deploy.mutateAsync({ ...t, releaseId: release.release_id, force }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
    if (ok) {
      toast.success(
        `Re-deployed ${release.release_id} to ${
          targetKind === 'thing-group' ? ENV_GROUP_LABEL[group] : `${ok} device${ok === 1 ? '' : 's'}`
        }.`,
      );
    }
    for (const f of failed) toast.error(describeDeployError(f.reason));
    if (ok && !failed.length) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-modal-top">
      <div className="bg-bg rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-auto p-5 space-y-4">
        <div>
          <h3 className="font-semibold">Deploy release {release.release_id} again</h3>
          <p className="text-xs text-muted">
            Sends the exact same parts that {release.release_id} published — nothing is recomposed.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setTargetKind('thing-group')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
              targetKind === 'thing-group' ? 'bg-accent text-white border-accent' : 'border-border hover:bg-soft'
            }`}
          >
            Device group
          </button>
          <button
            type="button"
            onClick={() => setTargetKind('device')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
              targetKind === 'device' ? 'bg-accent text-white border-accent' : 'border-border hover:bg-soft'
            }`}
          >
            Individual devices
          </button>
        </div>

        {targetKind === 'thing-group' ? (
          <select
            value={group}
            onChange={(e) => setGroup(e.target.value as EnvGroup)}
            className="px-2 py-1.5 border border-border rounded-md text-sm"
          >
            {DEPLOY_GROUPS.map((g) => (
              <option key={g} value={g}>{ENV_GROUP_LABEL[g]}</option>
            ))}
          </select>
        ) : (
          <DevicePicker
            devices={devices.data?.devices ?? []}
            groupOf={groupOf}
            selected={things}
            onChange={setThings}
            refresh={() => void devices.refetch()}
            isFetching={devices.isFetching}
          />
        )}

        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
          Apply immediately where supported (otherwise it applies at the next mains connection)
        </label>

        {targetKind === 'thing-group' && capMembers.length === 0 && (
          <p className="text-xs text-warn">
            {ENV_GROUP_LABEL[group]} has no devices — this re-deploy will be a no-op (SNAPSHOT
            job with no current members).
          </p>
        )}
        {targetKind === 'thing-group' && hardwareVerdict.kind !== 'ok' && (
          <div
            className={`text-xs rounded-md border px-3 py-2 ${
              hardwareVerdict.kind === 'block'
                ? 'border-danger text-danger'
                : 'border-warn text-warn'
            }`}
          >
            <span className="font-medium">
              {hardwareVerdict.kind === 'block' ? 'Wrong hardware — ' : 'Mixed hardware — '}
            </span>
            {hardwareVerdict.message}
            {hardwareVerdict.buckets.some((b) => b.assumed > 0) && (
              <span className="block mt-1 opacity-80">
                {hardwareVerdict.buckets.reduce((n, b) => n + b.assumed, 0)} of these are
                assumed rather than reported — they have sent no hardware type and carry no
                thing attribute, so the counts above may be optimistic.
              </span>
            )}
          </div>
        )}
        {targetKind === 'thing-group' && coverageVerdict.kind !== 'ok' && (
          <div
            className={`text-xs rounded-md border px-3 py-2 ${
              coverageVerdict.kind === 'none'
                ? 'border-danger text-danger'
                : 'border-warn text-warn'
            }`}
          >
            <span className="font-medium">
              {coverageVerdict.kind === 'none'
                ? 'Covers no device here — '
                : 'Partial hardware coverage — '}
            </span>
            {coverageVerdict.message}
            {coverageVerdict.kind === 'partial' && (
              <label className="flex items-center gap-2 mt-2 font-medium">
                <input
                  type="checkbox"
                  checked={ackCoverage}
                  onChange={(e) => setAckCoverage(e.target.checked)}
                />
                I mean to update only {coverageVerdict.covered.join(', ')} for now
              </label>
            )}
          </div>
        )}
        {targetKind === 'thing-group' && capMembers.length > 0 && (
          <CapabilityPreviewBlock
            preview={preview}
            isLoading={capability.isLoading}
            error={capability.error as Error | null}
            forced={force && !hasOs}
            needsAck={needsAck}
            ack={ackStageOnly}
            onAck={setAckStageOnly}
          />
        )}
        {quotaThings.length > 0 && (
          <SimQuotaPreviewBlock
            buckets={quotaBuckets}
            sims={simBatch.data?.sims}
            estimate={sizeEstimate}
            sourceNote={`Sizes are looked up now, from the registry, for the parts that`
              + ` ${release.release_id} froze — the same set the backend re-resolves.`}
            isLoading={simBatch.isLoading}
            unavailable={simBatch.isError}
            needsAck={needsQuotaAck}
            ack={ackQuota}
            onAck={setAckQuota}
          />
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void run()}
            disabled={!canDeploy}
            className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium disabled:opacity-50"
          >
            {deploy.isPending ? 'Deploying…' : `Deploy${targetCount > 0 ? ` (${targetCount})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Compose + deploy — the primary action.
// ─────────────────────────────────────────────────────────────────────────
function ComposeSection() {
  const toast = useToast();
  const osArtifacts = useReleaseArtifacts('os');
  const appArtifacts = useReleaseArtifacts('app');
  const fwArtifacts = useReleaseArtifacts('fw');
  const profiles = useContentProfiles();
  const devices = useDevices();
  const deviceGroups = useDeviceGroups();
  const deploy = useDeployRelease();

  const [sel, setSel] = useState<PartSelection>({
    osId: '',
    contentProfileId: '',
    appId: '',
    ucfwId: '',
  });
  const [force, setForce] = useState(false);
  const [ackStageOnly, setAckStageOnly] = useState(false);
  // DE-223 — the public release ID. EMPTY by default and that is the common
  // case: most Portal deploys are internal iterations, and a release ID names a
  // PUBLISHED release. Prefilling would silently burn the next ID (they are
  // immutable, and the registry drives the "next free" suggestion) on every
  // test deploy — so publishing is an explicit act: click the suggestion.
  const [releaseId, setReleaseId] = useState('');
  const nextId = useNextReleaseId();
  const releaseIdError = releaseId.trim() ? explainInvalidReleaseId(releaseId) : null;
  const [targetKind, setTargetKind] = useState<TargetKind>('thing-group');
  const [group, setGroup] = useState<EnvGroup>(DEPLOY_GROUPS[0]!);
  const [things, setThings] = useState<Set<string>>(new Set());

  const hasOs = Boolean(sel.osId);
  const hasContent = Boolean(sel.contentProfileId);
  const hasApp = Boolean(sel.appId);
  const hasUcfw = Boolean(sel.ucfwId);

  // thing → env-group (for the DevicePicker's group column/filter).
  const groupOf = useMemo(() => {
    const m = new Map<string, EnvGroup>();
    for (const g of deviceGroups.data?.groups ?? []) for (const t of g.devices) m.set(t, g.name);
    return m;
  }, [deviceGroups.data]);

  const membersByGroup = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const g of deviceGroups.data?.groups ?? []) m.set(g.name, g.devices);
    return m;
  }, [deviceGroups.data]);

  // Profiles grouped name → versions (backend returns name-asc, created-desc).
  const profilesByName = useMemo(() => {
    const m = new Map<string, ContentProfile[]>();
    for (const p of profiles.data?.profiles ?? []) {
      const arr = m.get(p.name) ?? [];
      arr.push(p);
      m.set(p.name, arr);
    }
    return m;
  }, [profiles.data]);

  // ── Meaningful-combination rule (client-side mirror of the backend 400) ──
  // OS present → app + ucfw are disabled (they ship inside the image); only
  // content may accompany it. No OS → require ≥1 of {app, content, ucfw}.
  const osLocksOthers = hasOs;
  const comboError = (() => {
    if (hasOs) return null; // os (+ optional content) is always valid
    if (!hasApp && !hasContent && !hasUcfw)
      return 'Select at least one part: an OS image, or any of content / app / firmware.';
    return null;
  })();

  // The parts we'll send — when OS is selected, app/ucfw are dropped even if a
  // stale selection lingers (the UI greys them out, this is belt-and-braces).
  const partsToSend: ReleaseDeployParts = useMemo(() => {
    const p: ReleaseDeployParts = {};
    if (hasOs) {
      p.os = { artifactId: sel.osId };
      if (hasContent) p.content = { profileId: sel.contentProfileId };
    } else {
      if (hasContent) p.content = { profileId: sel.contentProfileId };
      if (hasApp) p.app = { artifactId: sel.appId };
      if (hasUcfw) p.ucfw = { artifactId: sel.ucfwId };
    }
    return p;
  }, [hasOs, hasContent, hasApp, hasUcfw, sel]);

  const partCount = Object.keys(partsToSend).length;

  // Targets to fan out over.
  const targets: { targetKind: TargetKind; targetId: string }[] =
    targetKind === 'thing-group'
      ? [{ targetKind: 'thing-group', targetId: group }]
      : [...things].map((t) => ({ targetKind: 'device' as const, targetId: t }));

  const targetCount = targets.length;

  const groupEmpty =
    targetKind === 'thing-group' && (membersByGroup.get(group)?.length ?? 0) === 0;

  // Pre-deploy, group-SCOPED capability preview — buckets only the selected
  // group's current members (passed from useDeviceGroups), so it replaces the
  // misleading tenant-wide "+20 devices don't support" count.
  const capMembers = targetKind === 'thing-group' ? membersByGroup.get(group) ?? [] : [];
  const capability = useReleaseCapability({
    targetKind,
    targetId: targetKind === 'thing-group' ? group : null,
    members: capMembers,
    force,
    hasOs,
    enabled: targetKind === 'thing-group' && partCount > 0 && !groupEmpty,
  });
  const preview = capability.data;
  // Force-confirm guard (user decision): a FORCED non-OS deploy that would leave
  // members merely staged (older firmware) or entirely ignored must be explicitly
  // acknowledged before it can go out.
  const needsAck =
    targetKind === 'thing-group' &&
    !!preview &&
    preview.forcedNonOs &&
    (preview.buckets.stageOnly.length > 0 || preview.buckets.unsupported.length > 0);
  // DE-266 — SIM quota preview. Covers BOTH target kinds (unlike capability,
  // which is group-only): the SPA supplies the thing list either way, so the
  // group-membership asymmetry is already solved here.
  const quotaThings = targetKind === 'thing-group' ? capMembers : [...things];
  const simBatch = useSimBatch(quotaThings, partCount > 0 && quotaThings.length > 0);
  const sizeEstimate = useMemo(
    () =>
      estimateReleaseBytes(
        partsToSend,
        osArtifacts.data?.artifacts ?? [],
        appArtifacts.data?.artifacts ?? [],
        fwArtifacts.data?.artifacts ?? [],
        profiles.data?.profiles ?? [],
      ),
    [partsToSend, osArtifacts.data, appArtifacts.data, fwArtifacts.data, profiles.data],
  );
  const quotaBuckets = useMemo(
    () => bucketQuota(quotaThings, simBatch.data?.sims, sizeEstimate),
    // quotaThings is rebuilt per render; key on its content instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quotaThings.join(','), simBatch.data, sizeEstimate],
  );
  // Ack-gate only on states where the OTA would actually not arrive: a cap
  // already hit, or provably too little headroom. `tight`/`unknown` warn only.
  const needsQuotaAck =
    quotaBuckets.blocked.length > 0 || quotaBuckets.insufficient.length > 0;
  const [ackQuota, setAckQuota] = useState(false);

  // Re-arm the acks whenever the deploy shape changes (never carry a stale tick).
  const deployShapeSig =
    `${targetKind}|${group}|${force}|${hasOs}|${partCount}|${capMembers.length}`
    + `|${quotaThings.length}|${sizeEstimate.bytes}|${sizeEstimate.confidence}`
    + `|${quotaBuckets.blocked.length}:${quotaBuckets.insufficient.length}`;
  useEffect(() => {
    setAckStageOnly(false);
    setAckQuota(false);
  }, [deployShapeSig]);

  const canDeploy =
    !comboError && !releaseIdError && partCount > 0 && targetCount > 0
    && !deploy.isPending && (!needsAck || ackStageOnly)
    && (!needsQuotaAck || ackQuota);

  const reset = () => {
    setSel({ osId: '', contentProfileId: '', appId: '', ucfwId: '' });
    setForce(false);
    setAckStageOnly(false);
    setAckQuota(false);
    setThings(new Set());
    setReleaseId('');
  };

  const runDeploy = async () => {
    if (comboError) {
      toast.error(comboError);
      return;
    }
    if (partCount === 0) {
      toast.error('Compose a release first — select at least one part.');
      return;
    }
    if (targetCount === 0) {
      toast.error(targetKind === 'device' ? 'Pick at least one device.' : 'Pick a device group.');
      return;
    }
    if (releaseIdError) {
      toast.error(releaseIdError);
      return;
    }
    const results = await Promise.allSettled(
      targets.map((t) =>
        deploy.mutateAsync({
          targetKind: t.targetKind,
          targetId: t.targetId,
          parts: partsToSend,
          // Omitted entirely when blank — an unlabelled internal deploy.
          ...(releaseId.trim() ? { releaseId: releaseId.trim() } : {}),
          // OS: skip the on-device prompt. Non-OS (DE-186): apply immediately
          // (the backend 409s a forced single-device deploy to firmware that
          // would silently ignore it; groups get advisory counts back).
          force,
        }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled') as PromiseFulfilledResult<
      Awaited<ReturnType<typeof deploy.mutateAsync>>
    >[];
    const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];

    if (ok.length) {
      const first = ok[0]!.value;
      // The scoped pre-deploy preview already told the operator what to expect
      // per device (no misleading tenant-wide counts here); the Deployments
      // ledger shows the actual per-device outcome once the job runs.
      const where =
        targetKind === 'thing-group'
          ? ENV_GROUP_LABEL[group]
          : `${ok.length} device${ok.length === 1 ? '' : 's'}`;
      toast.success(
        first.releaseId
          ? `Deployed release ${first.releaseId} (${first.releaseLabel}) to ${where} — `
            + 'see Deployments below for per-device results.'
          : `Deployed ${first.releaseLabel} to ${where} (no release ID) — `
            + 'see Deployments below for per-device results.',
      );
    }
    for (const f of failed) {
      toast.error(describeDeployError(f.reason));
    }
    if (ok.length && !failed.length) reset();
  };

  const loadingAny =
    osArtifacts.isLoading ||
    appArtifacts.isLoading ||
    fwArtifacts.isLoading ||
    profiles.isLoading;

  return (
    <section className="card space-y-5">
      <div>
        <h2 className="font-medium">Compose &amp; deploy</h2>
        <p className="text-sm text-muted">
          Pick the parts to bundle, choose a target, then deploy. Devices apply the bundle at their
          next mains connection — or immediately when you force the install below (an OS install
          can prompt on-device unless skipped).
        </p>
      </div>

      {loadingAny && <p className="text-sm text-muted">Loading artifacts…</p>}

      {/* Part selectors */}
      <div className="grid gap-3 md:grid-cols-2">
        <PartSelect
          label="OS image"
          hint="Mender .mender artifact — installs the full system image."
          artifacts={osArtifacts.data?.artifacts ?? []}
          error={osArtifacts.error as Error | null}
          value={sel.osId}
          onChange={(osId) => setSel((s) => ({ ...s, osId }))}
          optionLabel={(a) =>
            `${a.version}${a.device_type ? ` · ${a.device_type}` : ''}` +
            // DE-181 — what the image ships, when recorded at registration.
            `${a.embedded_app_version ? ` · app ${a.embedded_app_version}` : ''}` +
            `${a.embedded_ucfw_version ? ` · fw ${a.embedded_ucfw_version}` : ''}`
          }
        />

        <ContentProfileSelect
          byName={profilesByName}
          error={profiles.error as Error | null}
          value={sel.contentProfileId}
          onChange={(contentProfileId) => setSel((s) => ({ ...s, contentProfileId }))}
        />

        <PartSelect
          label="App bundle"
          hint={
            osLocksOthers
              ? 'Ships inside the OS image — not selectable with an OS part.'
              : 'QML app bundle — staged to /data, applied at next power-on.'
          }
          artifacts={appArtifacts.data?.artifacts ?? []}
          error={appArtifacts.error as Error | null}
          value={sel.appId}
          onChange={(appId) => setSel((s) => ({ ...s, appId }))}
          disabled={osLocksOthers}
          optionLabel={(a) => `v${a.version}`}
        />

        <PartSelect
          label="uC firmware"
          hint={
            osLocksOthers
              ? 'Ships inside the OS image — not selectable with an OS part.'
              : 'Microcontroller .bin — delivered + verified device-side.'
          }
          artifacts={fwArtifacts.data?.artifacts ?? []}
          error={fwArtifacts.error as Error | null}
          value={sel.ucfwId}
          onChange={(ucfwId) => setSel((s) => ({ ...s, ucfwId }))}
          disabled={osLocksOthers}
          optionLabel={(a) => `v${a.version}`}
        />
      </div>

      {/* Combination rule feedback */}
      {osLocksOthers && (
        <p className="text-xs text-muted">
          An OS image is selected — app &amp; firmware ship inside the image, so they're disabled.
          Only content can be bundled with an OS release.
        </p>
      )}
      {comboError && <p className="text-sm text-warn">{comboError}</p>}

      {/* force — OS: skip the install prompt; non-OS (DE-186): apply immediately */}
      <label
        className={`flex items-center gap-2 text-sm ${partCount > 0 ? '' : 'opacity-50'}`}
        title={
          hasOs
            ? 'Install the OS image immediately without the on-device confirmation prompt.'
            : 'Apply on the running device as soon as it is idle — briefly restarts the demo UI '
              + '(a uC-firmware part flashes without a restart). Devices on older firmware stage '
              + 'instead and apply at the next mains connection.'
        }
      >
        <input
          type="checkbox"
          checked={force}
          disabled={partCount === 0}
          onChange={(e) => setForce(e.target.checked)}
        />
        {hasOs
          ? 'Skip the on-device prompt (install immediately)'
          : 'Force install — apply immediately on the running device'}
      </label>
      {force && !hasOs && (
        <p className="text-xs text-muted">
          The device waits for an idle moment (no demo running), then applies and briefly restarts
          the demo UI. If it stays busy or is mid-OS-update, the release stays staged and installs
          at the next mains connection instead.
        </p>
      )}

      {/* Target picker */}
      <div className="space-y-3 border-t border-border pt-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted">Target</div>
        <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setTargetKind('thing-group')}
            className={`px-3 py-1.5 font-medium ${
              targetKind === 'thing-group' ? 'bg-accent text-white' : 'bg-white hover:bg-soft'
            }`}
          >
            Device group
          </button>
          <button
            type="button"
            onClick={() => setTargetKind('device')}
            className={`px-3 py-1.5 font-medium border-l border-border ${
              targetKind === 'device' ? 'bg-accent text-white' : 'bg-white hover:bg-soft'
            }`}
          >
            Device
          </button>
        </div>

        {targetKind === 'thing-group' ? (
          <div className="space-y-1">
            <label className="block max-w-xs">
              <span className="text-xs text-muted">Group</span>
              <select
                value={group}
                onChange={(e) => setGroup(e.target.value as EnvGroup)}
                className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-white"
              >
                {DEPLOY_GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {ENV_GROUP_LABEL[g]} ({membersByGroup.get(g)?.length ?? 0})
                  </option>
                ))}
              </select>
            </label>
            {groupEmpty && (
              <p className="text-xs text-warn">
                {ENV_GROUP_LABEL[group]} has no devices — this deploy will be a no-op (SNAPSHOT job
                with no current members).
              </p>
            )}
          </div>
        ) : (
          <DevicePicker
            devices={devices.data?.devices ?? []}
            groupOf={groupOf}
            selected={things}
            onChange={setThings}
            refresh={() => void devices.refetch()}
            isFetching={devices.isFetching}
          />
        )}
      </div>

      {/* Summary + deploy */}
      <div className="border-t border-border pt-4 space-y-3">
        <ReleaseSummary
          parts={partsToSend}
          osArtifacts={osArtifacts.data?.artifacts ?? []}
          appArtifacts={appArtifacts.data?.artifacts ?? []}
          fwArtifacts={fwArtifacts.data?.artifacts ?? []}
          profilesByName={profilesByName}
          targetKind={targetKind}
          group={group}
          deviceCount={things.size}
        />
        {/* DE-223 — public release ID. Optional on purpose: blank means an
            unlabelled internal deploy, which is the common case. Publishing is
            the deliberate act of naming it, so the suggestion is one click
            away rather than pre-filled (a prefill would burn an immutable ID
            on every test deploy). */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <label htmlFor="release-id" className="text-xs font-medium text-muted w-28 shrink-0">
              Release ID
            </label>
            <input
              id="release-id"
              type="text"
              value={releaseId}
              onChange={(e) => setReleaseId(e.target.value)}
              placeholder="none — internal deploy"
              aria-invalid={releaseIdError ? true : undefined}
              aria-describedby="release-id-help"
              className={`px-2 py-1 border rounded-md text-sm font-mono w-40 ${
                releaseIdError ? 'border-red-500' : 'border-border'
              }`}
            />
            {nextId.data?.releaseId && releaseId.trim() !== nextId.data.releaseId && (
              <button
                type="button"
                onClick={() => setReleaseId(nextId.data!.releaseId)}
                className="px-2 py-1 border border-border rounded-md text-xs font-medium hover:bg-soft"
                title="Publish this deploy under the next free release ID"
              >
                Use {nextId.data.releaseId}
              </button>
            )}
            {releaseId.trim() && (
              <button
                type="button"
                onClick={() => setReleaseId('')}
                className="px-2 py-1 text-xs text-muted hover:underline"
              >
                Clear
              </button>
            )}
          </div>
          <p id="release-id-help" className="text-xs text-muted pl-30">
            {releaseIdError ? (
              <span className="text-red-600">{releaseIdError}</span>
            ) : releaseId.trim() ? (
              <>
                Publishes as <span className="font-mono">{releaseId.trim()}</span>. A release ID is
                immutable — re-using it with a different part set is rejected.
              </>
            ) : (
              'Leave blank for an internal deploy. Set it only when this is a published release.'
            )}
          </p>
        </div>
        {targetKind === 'thing-group' && partCount > 0 && !groupEmpty && (
          <CapabilityPreviewBlock
            preview={preview}
            isLoading={capability.isLoading}
            error={capability.error as Error | null}
            forced={force && !hasOs}
            needsAck={needsAck}
            ack={ackStageOnly}
            onAck={setAckStageOnly}
          />
        )}
        {partCount > 0 && quotaThings.length > 0 && (
          <SimQuotaPreviewBlock
            buckets={quotaBuckets}
            sims={simBatch.data?.sims}
            estimate={sizeEstimate}
            isLoading={simBatch.isLoading}
            unavailable={simBatch.isError}
            needsAck={needsQuotaAck}
            ack={ackQuota}
            onAck={setAckQuota}
          />
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={deploy.isPending}
            className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => void runDeploy()}
            disabled={!canDeploy}
            className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50"
          >
            {deploy.isPending
              ? 'Deploying…'
              : `Deploy${targetCount > 0 ? ` (${targetCount})` : ''}`}
          </button>
        </div>
      </div>
    </section>
  );
}

// A generic registry-artifact <select>. Empty value = "not included".
function PartSelect({
  label,
  hint,
  artifacts,
  error,
  value,
  onChange,
  optionLabel,
  disabled = false,
}: {
  label: string;
  hint: string;
  artifacts: ReleaseArtifact[];
  error: Error | null;
  value: string;
  onChange: (id: string) => void;
  optionLabel: (a: ReleaseArtifact) => string;
  disabled?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-fg">{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-white disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="">— none —</option>
        {artifacts.map((a) => (
          <option key={a.id} value={a.id}>
            {optionLabel(a)}
          </option>
        ))}
      </select>
      <span className="block text-[11px] text-muted mt-1">{hint}</span>
      {error && <span className="block text-[11px] text-warn mt-0.5">{error.message}</span>}
      {!error && artifacts.length === 0 && !disabled && (
        <span className="block text-[11px] text-muted/70 mt-0.5">No artifacts registered yet.</span>
      )}
    </label>
  );
}

// Content profile selector — two-step (name → version) like the content deploy.
function ContentProfileSelect({
  byName,
  error,
  value,
  onChange,
}: {
  byName: Map<string, ContentProfile[]>;
  error: Error | null;
  value: string; // selected profile id
  onChange: (profileId: string) => void;
}) {
  const names = useMemo(() => [...byName.keys()].sort(), [byName]);
  // Resolve the currently-selected profile to its name so the name <select>
  // reflects the chosen version.
  const selectedName = useMemo(() => {
    for (const [name, vers] of byName) if (vers.some((p) => p.id === value)) return name;
    return '';
  }, [byName, value]);
  const versions = selectedName ? byName.get(selectedName) ?? [] : [];

  return (
    <div className="block">
      <span className="text-xs font-medium text-fg">Content profile</span>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <select
          value={selectedName}
          onChange={(e) => {
            const n = e.target.value;
            // Default to the latest version of the picked name (index 0).
            onChange(n ? (byName.get(n) ?? [])[0]?.id ?? '' : '');
          }}
          className="block w-full px-3 py-2 border border-border rounded-md text-sm bg-white"
        >
          <option value="">— none —</option>
          {names.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <select
          value={value}
          disabled={!selectedName}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full px-3 py-2 border border-border rounded-md text-sm bg-white font-mono disabled:opacity-50"
        >
          {versions.map((p, i) => (
            <option key={p.id} value={p.id}>
              v{p.version}
              {i === 0 ? ' (latest)' : ''}
            </option>
          ))}
        </select>
      </div>
      <span className="block text-[11px] text-muted mt-1">
        A versioned content set from the{' '}
        <Link to="/admin/content" className="text-accent hover:underline">
          library
        </Link>
        . Factory-only profiles can't be deployed.
      </span>
      {error && <span className="block text-[11px] text-warn mt-0.5">{error.message}</span>}
    </div>
  );
}

// The human-readable composition that will be sent, plus the resolved target.
function ReleaseSummary({
  parts,
  osArtifacts,
  appArtifacts,
  fwArtifacts,
  profilesByName,
  targetKind,
  group,
  deviceCount,
}: {
  parts: ReleaseDeployParts;
  osArtifacts: ReleaseArtifact[];
  appArtifacts: ReleaseArtifact[];
  fwArtifacts: ReleaseArtifact[];
  profilesByName: Map<string, ContentProfile[]>;
  targetKind: TargetKind;
  group: EnvGroup;
  deviceCount: number;
}) {
  const partCount = Object.keys(parts).length;
  if (partCount === 0) {
    return <p className="text-xs text-muted">No parts selected yet.</p>;
  }
  const osArt = parts.os ? osArtifacts.find((a) => a.id === parts.os!.artifactId) : null;
  const osVer = osArt?.version ?? null;
  // DE-181 — what the OS image ships (bundled app + uC fw), when recorded.
  const osShips = osArt
    ? [
        osArt.embedded_app_version ? `app ${osArt.embedded_app_version}` : null,
        osArt.embedded_ucfw_version ? `fw ${osArt.embedded_ucfw_version}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  const appVer = parts.app ? appArtifacts.find((a) => a.id === parts.app!.artifactId)?.version : null;
  const ucfwVer = parts.ucfw
    ? fwArtifacts.find((a) => a.id === parts.ucfw!.artifactId)?.version
    : null;
  const contentLabel = (() => {
    if (!parts.content) return null;
    for (const vers of profilesByName.values()) {
      const p = vers.find((x) => x.id === parts.content!.profileId);
      if (p) return `${p.name} v${p.version}`;
    }
    return 'selected';
  })();

  const targetText =
    targetKind === 'thing-group'
      ? ENV_GROUP_LABEL[group]
      : `${deviceCount} device${deviceCount === 1 ? '' : 's'}`;

  return (
    <div className="text-sm">
      <span className="text-muted">Release: </span>
      <span className="flex flex-wrap gap-1.5 mt-1">
        {osVer && (
          <span className="pill-accent">
            OS {osVer}
            {osShips && <span className="opacity-75"> (ships {osShips})</span>}
          </span>
        )}
        {contentLabel && <span className="pill-accent">content {contentLabel}</span>}
        {appVer && <span className="pill-accent">app v{appVer}</span>}
        {ucfwVer && <span className="pill-accent">fw v{ucfwVer}</span>}
        <span className="pill-muted">→ {targetText}</span>
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Artifact registries — read-only.
// ─────────────────────────────────────────────────────────────────────────
function RegistriesSection() {
  return (
    <section className="card space-y-4">
      <div>
        <h2 className="font-medium">Artifact registries</h2>
        <p className="text-sm text-muted">
          What's available to bundle. Upload here, or via the{' '}
          <code className="font-mono text-xs bg-soft px-1 py-0.5 rounded">
            scripts/upload-&#123;os,app,fw&#125;.sh
          </code>{' '}
          CLIs (CI). The app bundle zip comes from{' '}
          <code className="font-mono text-xs bg-soft px-1 py-0.5 rounded">
            scripts/yocto-fetch-image.sh --app-zip
          </code>.
        </p>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <RegistryTable type="os" title="OS images" />
        <RegistryTable type="app" title="App bundles" />
        <RegistryTable type="fw" title="uC firmware" />
      </div>
    </section>
  );
}

function RegistryTable({ type, title }: { type: ArtifactType; title: string }) {
  const q = useReleaseArtifacts(type);
  const rows = q.data?.artifacts ?? [];
  const [uploadOpen, setUploadOpen] = useState(false);
  return (
    <div className="border border-border rounded-md p-3">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="font-semibold text-sm">{title}</h3>
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-muted">{rows.length}</span>
          <button
            onClick={() => setUploadOpen(true)}
            className="text-xs text-accent hover:underline"
          >
            Upload
          </button>
        </div>
      </div>
      {uploadOpen && (
        <ArtifactUploadModal type={type} title={title} onClose={() => setUploadOpen(false)} />
      )}
      {q.isLoading && <p className="text-xs text-muted">Loading…</p>}
      {q.error && <p className="text-xs text-warn">{(q.error as Error).message}</p>}
      {!q.isLoading && !q.error && rows.length === 0 && (
        <p className="text-xs text-muted">None registered.</p>
      )}
      {rows.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted border-b border-border">
              <th className="font-medium py-1 pr-3">Version</th>
              {type === 'os' && <th className="font-medium py-1 pr-3">Target</th>}
              {type === 'os' && <th className="font-medium py-1 pr-3">Ships</th>}
              <th className="font-medium py-1 pr-3">Size</th>
              <th className="font-medium py-1">Added</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-b border-border last:border-0">
                <td className="py-1 pr-3 font-mono" title={a.notes ?? undefined}>
                  {a.version}
                </td>
                {type === 'os' && (
                  <td className="py-1 pr-3">{a.device_type ?? '—'}</td>
                )}
                {type === 'os' && (
                  // DE-181 — bundled app + uC fw, recorded at registration.
                  <td className="py-1 pr-3">
                    {a.embedded_app_version || a.embedded_ucfw_version
                      ? [
                          a.embedded_app_version ? `app ${a.embedded_app_version}` : null,
                          a.embedded_ucfw_version ? `fw ${a.embedded_ucfw_version}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      : '—'}
                  </td>
                )}
                <td className="py-1 pr-3">{a.bytes != null ? fmtBytes(a.bytes) : '—'}</td>
                <td className="py-1 text-muted" title={a.created_at}>
                  {fmtAgo(a.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Artifact upload (DE-171 2F). Presign → direct-to-S3 PUT (XHR, progress) →
// register. os: .mender (version prefilled from the filename + device type);
// fw: .bin (+ version); app: .zip (version read server-side from app-version.txt).
// ─────────────────────────────────────────────────────────────────────────
const ARTIFACT_ACCEPT: Record<ArtifactType, string> = {
  os: '.mender',
  app: '.zip',
  fw: '.bin',
};

// Pull the mender artifact name out of a built .mender filename, e.g.
// core-image-base-artomeui-r110x-artomeui-v1.20.12-aws.70.mender → artomeui-v1.20.12-aws.70.
function osNameFromFilename(name: string): string {
  const m = name.match(/(artomeui-v[\w.\-]+?)\.mender$/i);
  return m?.[1] ?? name.replace(/\.mender$/i, '');
}

// DE-307 — the same filename also names the BOARD, and the upload now needs it:
// the device type is part of the S3 key. The build writes
// core-image-base-<device-type>-<artifact-name>.mender, so the type sits between
// the image basename and the artifact name.
//
// A hint, never an assertion. Returning '' leaves the operator's selection
// alone rather than pre-filling a guess, and the authority remains the artifact
// header — which is what upload-os.sh reads and what the backend validates the
// key against.
export function osDeviceTypeFromFilename(name: string): string {
  const m = name.match(/core-image-base-(artomeui-[a-z0-9]+)-/i);
  return m?.[1]?.toLowerCase() ?? '';
}

function ArtifactUploadModal({
  type,
  title,
  onClose,
}: {
  type: ArtifactType;
  title: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const presign = useArtifactUploadUrl();
  const register = useRegisterArtifact();
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  // DE-306 — no literal default. Empty until the registry loads, then the
  // first active board. A hardcoded default is how the short form survived
  // in three places for a year.
  const hardwareTypes = useHardwareTypes();
  const [deviceType, setDeviceType] = useState('');
  useEffect(() => {
    if (deviceType) return;
    const first = (hardwareTypes.data?.hardwareTypes ?? []).find((h) => h.active);
    if (first) setDeviceType(first.id);
  }, [hardwareTypes.data, deviceType]);
  // DE-181 — optional: what the OS image ships (bundled app + uC fw). Advisory
  // display metadata; blank → registered as null and shown as "—".
  const [shipsApp, setShipsApp] = useState('');
  const [shipsUcfw, setShipsUcfw] = useState('');
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'registering'>('idle');
  const [progress, setProgress] = useState(0);
  const busy = phase !== 'idle';
  const needsVersion = type !== 'app'; // app version comes from the zip's app-version.txt

  const onPick = (f: File | null) => {
    setFile(f);
    if (f && type === 'os' && !version) setVersion(osNameFromFilename(f.name));
    // DE-307 — the filename also names the board, and the device type is now
    // part of the S3 key. Pre-fill it only when the file actually says so and
    // it is a type the registry knows, so a rename or an unlisted board leaves
    // the operator's own choice standing rather than replacing it with a guess.
    if (f && type === 'os') {
      const hinted = osDeviceTypeFromFilename(f.name);
      if (hinted && (hardwareTypes.data?.hardwareTypes ?? []).some((h) => h.id === hinted))
        setDeviceType(hinted);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return toast.error('Pick a file to upload.');
    if (needsVersion && !version.trim()) return toast.error('Enter a version.');
    try {
      setPhase('uploading');
      setProgress(0);
      const { uploadUrl, s3Key } = await presign.mutateAsync({
        type,
        filename: file.name,
        // OS: the backend DERIVES the key from these two — os/<type>/<name>.mender
        // (DE-307) — and then refuses a registration whose s3Key disagrees, so
        // this is the only place the key comes from. The CLIs ask the same route.
        ...(type === 'os' ? { artifactName: version.trim(), deviceType } : {}),
      });
      await putToS3(uploadUrl, file, setProgress);
      setPhase('registering');
      const body: { type: ArtifactType } & Record<string, unknown> =
        type === 'app'
          ? { type, zipKey: s3Key }
          : type === 'os'
            ? {
                type, version: version.trim(), artifactName: version.trim(), deviceType, s3Key,
                // DE-181 — only send when filled; backend stores null otherwise.
                ...(shipsApp.trim() ? { embeddedAppVersion: shipsApp.trim() } : {}),
                ...(shipsUcfw.trim() ? { embeddedUcfwVersion: shipsUcfw.trim() } : {}),
              }
            : { type, version: version.trim(), s3Key };
      const { artifact } = await register.mutateAsync(body);
      toast.success(`Registered ${title} ${artifact.version}.`);
      onClose();
    } catch (err) {
      toast.error(`Upload failed: ${err instanceof ApiError ? err.message : (err as Error).message}`);
      setPhase('idle');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal">
      <form onSubmit={submit} className="card w-full max-w-md space-y-4">
        <div>
          <h2 className="font-semibold">Upload {title.toLowerCase()}</h2>
          <p className="text-sm text-muted">
            {type === 'os' && 'A Mender .mender image — uploaded straight to S3 (can be large).'}
            {type === 'app' && 'A QML app bundle .zip (qml/ + assets/ + app-version.txt). Build it with scripts/yocto-fetch-image.sh --app-zip. The version is read from the zip.'}
            {type === 'fw' && 'A microcontroller firmware .bin. The device verifies it on apply.'}
          </p>
        </div>

        <input
          type="file"
          accept={ARTIFACT_ACCEPT[type]}
          disabled={busy}
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          className="block w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-md
                     file:border-0 file:bg-soft file:text-fg file:font-medium"
        />
        {file && (
          <p className="text-xs text-muted">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</p>
        )}

        {needsVersion && (
          <label className="block text-sm">
            <span className="text-muted">{type === 'os' ? 'Artifact name' : 'Version'}</span>
            <input
              type="text"
              value={version}
              disabled={busy}
              onChange={(e) => setVersion(e.target.value)}
              placeholder={type === 'os' ? 'artomeui-v1.20.12-aws.71' : '5.4.1'}
              className="mt-1 block w-full border border-border rounded-md px-2 py-1 text-sm font-mono"
            />
          </label>
        )}
        {type === 'os' && (
          <label className="block text-sm">
            <span className="text-muted">Device type</span>
            <select
              value={deviceType}
              disabled={busy}
              onChange={(e) => setDeviceType(e.target.value)}
              className="mt-1 block w-full border border-border rounded-md px-2 py-1 text-sm"
            >
              {/* DE-306: from the registry, never a literal. The display name
                  comes from the registry too — a formatting function here would
                  be a second place that has to learn about a new board. */}
              {(hardwareTypes.data?.hardwareTypes ?? [])
                .filter((h) => h.active)
                .map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.display_name} ({h.id})
                  </option>
                ))}
            </select>
          </label>
        )}
        {type === 'os' && (
          /* DE-181 — what the image ships (bundled app + uC fw). Optional,
             display-only; find the values in the build tree (artomeui.bb
             ARTOMEUI_APP_VERSION / uc-firmware app_update_<ver>.bin). */
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-sm">
              <span className="text-muted">Ships app version</span>
              <input
                type="text"
                value={shipsApp}
                disabled={busy}
                onChange={(e) => setShipsApp(e.target.value)}
                placeholder="1.9.1 (optional)"
                className="mt-1 block w-full border border-border rounded-md px-2 py-1 text-sm font-mono"
              />
            </label>
            <label className="block text-sm">
              <span className="text-muted">Ships uC-fw version</span>
              <input
                type="text"
                value={shipsUcfw}
                disabled={busy}
                onChange={(e) => setShipsUcfw(e.target.value)}
                placeholder="5.4.9 (optional)"
                className="mt-1 block w-full border border-border rounded-md px-2 py-1 text-sm font-mono"
              />
            </label>
          </div>
        )}

        {phase === 'uploading' && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted"><span>Uploading…</span><span>{progress}%</span></div>
            <div className="w-full bg-soft rounded-full h-2">
              <div className="bg-accent h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
        {phase === 'registering' && (
          <p className="text-sm text-muted">
            {type === 'app' ? 'Unpacking + content-addressing the bundle…' : 'Registering…'}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" disabled={!file || busy}
            className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50">
            {phase === 'uploading' ? 'Uploading…' : phase === 'registering' ? 'Registering…' : 'Upload'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Deployments ledger — the CURRENT release per target.
// ─────────────────────────────────────────────────────────────────────────
function DeploymentsSection() {
  const q = useReleaseDeployments();
  const rows = q.data?.deployments ?? [];
  // Live IoT job status overlay (admin Lambda). The ledger's job_status column
  // is never written, so the real status comes from here, keyed by job_id.
  const statusQ = useReleaseStatuses(q.data?.deployments);
  const statuses = statusQ.data?.statuses ?? {};

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-medium">Deployments</h2>
        <p className="text-sm text-muted">
          The current release per target (one row per device or group; a re-deploy supersedes the
          prior row).
        </p>
      </div>
      {q.isLoading && <p className="text-sm text-muted">Loading…</p>}
      {q.error && <p className="text-sm text-warn">{(q.error as Error).message}</p>}
      {!q.isLoading && !q.error && rows.length === 0 && (
        <p className="text-sm text-muted">No releases deployed yet.</p>
      )}
      {rows.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted border-b border-border">
              <th className="font-medium py-1.5 pr-4">Target</th>
              <th className="font-medium py-1.5 pr-4">Release</th>
              <th className="font-medium py-1.5 pr-4">Job</th>
              <th className="font-medium py-1.5 pr-4">Status</th>
              <th className="font-medium py-1.5">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <DeploymentRow
                key={`${d.target_kind}:${d.target_id}`}
                d={d}
                status={d.job_id ? statuses[d.job_id] : undefined}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function DeploymentRow({ d, status }: { d: ReleaseDeployment; status?: ReleaseJobStatus }) {
  const isGroup = d.target_kind === 'thing-group';
  const [expanded, setExpanded] = useState(false);
  const targetLabel = isGroup ? ENV_GROUP_LABEL[d.target_id as EnvGroup] ?? d.target_id : d.target_id;
  // Live status (admin Lambda) wins; fall back to the static ledger column, then "—".
  // For a group target also surface the per-thing rollout tally.
  // DE-186 — on SUCCEEDED, prefer the staging-outcome pill (Applied / Staged /
  // Staged only / Staged — apply failed) so a forced deploy that silently
  // degraded on old firmware is visible at a glance.
  const pill =
    status && status.status !== 'UNKNOWN'
      ? releaseOutcomePill(status.status, status.status_details, !!d.force) ?? jobPill(status)
      : null;
  const c = status?.counts;
  const groupTally =
    isGroup && c
      ? [
          c.succeeded ? `${c.succeeded}✓` : null,
          c.in_progress ? `${c.in_progress}…` : null,
          c.queued ? `${c.queued} queued` : null,
          c.failed || c.rejected || c.timed_out ? `${c.failed + c.rejected + c.timed_out}✗` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : '';
  // Group rows expand to per-device outcomes (worked / only-force-ignored /
  // ignored). Aggregate `counts` can't tell applied from staged-only (both are
  // SUCCEEDED), so the breakdown is fetched lazily on expand.
  const canExpand = isGroup && !!d.job_id;
  return (
    <>
      <tr className="border-b border-border last:border-0 hover:bg-soft">
        <td className="py-1.5 pr-4">
          <span className="flex items-center gap-1.5">
            {canExpand ? (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-4 text-xs text-muted hover:text-fg"
                title={expanded ? 'Hide per-device outcomes' : 'Show per-device outcomes'}
                aria-expanded={expanded}
              >
                {expanded ? '▾' : '▸'}
              </button>
            ) : (
              <span className="inline-block w-4" />
            )}
            <span className="pill-muted">{isGroup ? 'group' : 'device'}</span>
            {isGroup ? (
              <span>{targetLabel}</span>
            ) : (
              <Link
                to={`/devices/${encodeURIComponent(d.target_id)}`}
                className="font-mono text-xs hover:text-accent hover:underline"
              >
                {d.target_id}
              </Link>
            )}
          </span>
        </td>
        <td className="py-1.5 pr-4 font-mono text-xs" title={JSON.stringify(d.parts)}>
          <span className="flex items-center gap-1.5">
            {/* DE-223 — the public release ID is the headline when this deploy
                published one; the derived part label stays as the detail. An
                unlabelled internal deploy shows the label alone, as before. */}
            {d.release_id ? (
              <>
                <span className="font-semibold" title="Public release ID">{d.release_id}</span>
                <span className="text-muted">{d.release_label}</span>
              </>
            ) : (
              d.release_label
            )}
            {d.force && (
              <span
                className="pill-muted"
                title="Deployed with force — OS: prompt skipped; non-OS: immediate apply requested"
              >
                forced
              </span>
            )}
          </span>
        </td>
        <td className="py-1.5 pr-4 font-mono text-[11px] text-muted truncate max-w-[180px]" title={d.job_id ?? undefined}>
          {d.job_id ?? '—'}
        </td>
        <td className="py-1.5 pr-4">
          {pill ? (
            <span className="flex items-center gap-1.5">
              <span className={pill.cls} title={pill.title}>
                {pill.label}
              </span>
              {groupTally && <span className="text-[11px] text-muted">{groupTally}</span>}
            </span>
          ) : d.job_status ? (
            <span className="pill-muted">{d.job_status}</span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td className="py-1.5 text-muted" title={d.created_at}>
          {fmtAgo(d.created_at)}
        </td>
      </tr>
      {canExpand && expanded && (
        <tr className="bg-soft/30">
          <td colSpan={5} className="px-3 py-2">
            <GroupMemberBreakdown jobId={d.job_id!} force={!!d.force} />
          </td>
        </tr>
      )}
    </>
  );
}

// Per-device outcomes for ONE group release deploy — fetched lazily when the row
// is expanded. Buckets members into the three groups the operator asked for:
// worked / only-force-ignored (staged-later) / entirely-ignored, plus pending.
function GroupMemberBreakdown({ jobId, force }: { jobId: string; force: boolean }) {
  const q = useReleaseJobMembers(jobId, true);
  if (q.isLoading) return <p className="text-xs text-muted">Loading per-device outcomes…</p>;
  if (q.error) return <p className="text-xs text-warn">{(q.error as Error).message}</p>;
  const members = q.data?.members ?? [];
  if (members.length === 0)
    return <p className="text-xs text-muted">No device executions found for this job.</p>;
  const groups: Record<OutcomeBucket, ReleaseJobMember[]> = {
    worked: [], 'staged-later': [], ignored: [], pending: [],
  };
  for (const m of members) groups[memberOutcomeBucket(m.status, m.status_details, force)].push(m);
  return (
    <div className="space-y-2">
      <MemberBucket
        title="Worked" cls="text-accent" force={force} members={groups.worked}
        hint="applied now, or staged and will apply — the update took"
      />
      <MemberBucket
        title="Only the force was ignored" cls="text-warn" force={force} members={groups['staged-later']}
        hint="staged — applies at the next power-on or via on-device Apply Now (firmware older than aws.80). Not a failure."
      />
      <MemberBucket
        title="Entirely ignored" cls="text-warn" force={force} members={groups.ignored}
        hint="firmware predates the release op (rejected), or the job failed — update the OS first"
      />
      <MemberBucket
        title="Pending" cls="text-muted" force={force} members={groups.pending}
        hint="offline or not yet reported"
      />
      {q.data?.truncated && (
        <p className="text-[11px] text-muted">Showing the first {members.length} devices.</p>
      )}
    </div>
  );
}

function MemberBucket({
  title, hint, cls, members, force,
}: {
  title: string;
  hint: string;
  cls: string;
  members: ReleaseJobMember[];
  force: boolean;
}) {
  if (members.length === 0) return null;
  return (
    <div>
      <div className={`text-xs font-semibold ${cls}`}>
        {title} ({members.length})
      </div>
      <p className="text-[11px] text-muted">{hint}</p>
      <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
        {members.map((m) => {
          const p =
            releaseOutcomePill(m.status, m.status_details, force) ??
            jobPill({ status: m.status, status_details: m.status_details });
          return (
            <li key={m.thing} className="flex items-center gap-1.5">
              <Link
                to={`/devices/${encodeURIComponent(m.thing)}`}
                className="font-mono text-[11px] hover:text-accent hover:underline"
              >
                {m.thing}
              </Link>
              <span className={p.cls} title={p.title}>{p.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Pre-deploy, group-scoped capability summary — replaces the misleading
// tenant-wide count. Buckets are expandable to device names; a forced deploy
// that would leave members staged or unsupported requires an explicit ack.
function CapabilityPreviewBlock({
  preview, isLoading, error, forced, needsAck, ack, onAck,
}: {
  preview: CapabilityPreview | undefined;
  isLoading: boolean;
  error: Error | null;
  forced: boolean;
  needsAck: boolean;
  ack: boolean;
  onAck: (v: boolean) => void;
}) {
  if (isLoading) return <p className="text-xs text-muted">Checking device capabilities…</p>;
  if (error) return <p className="text-xs text-warn">Capability check failed: {error.message}</p>;
  if (!preview) return null;
  const b = preview.buckets;
  return (
    <div className="rounded-md border border-border bg-soft/40 p-3 space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">
        This release on {preview.total} device{preview.total === 1 ? '' : 's'}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        <CapBucket
          cls="text-accent" things={b.applyNow}
          label={forced ? 'Apply now' : 'Will apply (next power-on)'}
          hint="release accepted — takes effect"
        />
        {forced && (
          <CapBucket
            cls="text-warn" things={b.stageOnly} label="Stage only (force not applied)"
            hint="older firmware: stages the release and applies it at the next power-on / on-device Apply Now — not a failure"
          />
        )}
        <CapBucket
          cls="text-warn" things={b.unsupported} label="Can't take this release"
          hint="firmware predates the release op — nothing happens; update the OS first"
        />
        <CapBucket
          cls="text-muted" things={b.unknown} label="Unknown"
          hint="device hasn't reported its capability yet"
        />
      </div>
      {needsAck && (
        <label className="flex items-start gap-2 text-xs text-warn">
          <input type="checkbox" checked={ack} onChange={(e) => onAck(e.target.checked)} className="mt-0.5" />
          <span>
            I understand
            {b.stageOnly.length > 0 &&
              ` ${b.stageOnly.length} device${b.stageOnly.length === 1 ? '' : 's'} will stage this release and apply it at the next power-on (not now)`}
            {b.stageOnly.length > 0 && b.unsupported.length > 0 && ';'}
            {b.unsupported.length > 0 &&
              ` ${b.unsupported.length} device${b.unsupported.length === 1 ? '' : 's'} can't take this release at all`}
            .
          </span>
        </label>
      )}
    </div>
  );
}

function CapBucket({
  label, things, cls, hint,
}: {
  label: string;
  things: string[];
  cls: string;
  hint: string;
}) {
  if (things.length === 0) return null;
  return (
    <details className="min-w-0">
      <summary className={`cursor-pointer text-xs font-medium ${cls}`}>
        {label}: {things.length}
      </summary>
      <p className="text-[11px] text-muted mt-0.5 max-w-sm">{hint}</p>
      <ul className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
        {things.map((t) => (
          <li key={t}>
            <Link
              to={`/devices/${encodeURIComponent(t)}`}
              className="font-mono text-[11px] hover:text-accent hover:underline"
            >
              {t}
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}

// DE-266 — SIM quota preview: estimated OTA download vs each target device's
// remaining Onomondo data cap. Blocked/insufficient gate the deploy behind an
// explicit ack; tight/unknown warn only; unmanaged (Telia SIM, no modem, SIM
// outside our org) is informational. A vendor outage or unconfigured
// integration must never block a deploy — it collapses to one muted line.
//
// Mounted by BOTH deploy surfaces, so it knows nothing about which one it is on:
// everything context-specific arrives as the estimate (whose `confidence` drives
// the wording via describeEstimate) plus an optional sourceNote.
function SimQuotaPreviewBlock({
  buckets, sims, estimate, sourceNote, isLoading, unavailable, needsAck, ack, onAck,
}: {
  buckets: QuotaBuckets;
  sims: Record<string, DeviceSimStatus> | undefined;
  estimate: ReleaseSizeEstimate;
  sourceNote?: string;
  isLoading: boolean;
  unavailable: boolean;
  needsAck: boolean;
  ack: boolean;
  onAck: (v: boolean) => void;
}) {
  const [raiseFor, setRaiseFor] = useState<{ thing: string; sim: SimQuotaSummary } | null>(null);
  if (isLoading) return <p className="text-xs text-muted">Checking SIM data quotas…</p>;
  if (unavailable) {
    return (
      <p className="text-xs text-muted">
        SIM quota check unavailable — deploy is not blocked, but cellular devices near their
        data cap may stall mid-download.
      </p>
    );
  }
  const openRaise = (thing: string) => {
    const sim = sims?.[thing]?.sim;
    if (sim) setRaiseFor({ thing, sim });
  };
  const copy = describeEstimate(estimate);
  return (
    <div className="rounded-md border border-border bg-soft/40 p-3 space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">
        SIM data quota —{' '}
        <span className="normal-case font-normal">
          {copy.qualifier ? (
            <>
              estimated download {copy.qualifier}{' '}
              <strong className="font-semibold">{fmtBytes(estimate.bytes)}</strong> per device
            </>
          ) : (
            <>download size unknown</>
          )}
        </span>
      </div>
      {copy.caveat && <p className="text-[11px] text-warn">{copy.caveat}</p>}
      {sourceNote && <p className="text-[11px] text-muted">{sourceNote}</p>}
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        <QuotaBucket
          cls="text-warn" things={buckets.blocked} sims={sims} label="Cap already hit"
          hint="the SIM passes no data until its period resets — the OTA cannot download"
          onRaise={openRaise}
        />
        <QuotaBucket
          cls="text-warn" things={buckets.insufficient} sims={sims} label="Not enough quota"
          hint="remaining data is below the estimated download — the OTA would stall mid-transfer"
          onRaise={openRaise}
        />
        <QuotaBucket
          cls="text-warn" things={buckets.tight} sims={sims} label="Tight"
          hint="less than 20% headroom over the estimate — consider raising the cap first"
          onRaise={openRaise}
        />
        <QuotaBucket
          cls="text-accent" things={buckets.ok} sims={sims} label={copy.okLabel}
          hint={copy.okHint}
        />
        <QuotaBucket
          cls="text-muted" things={buckets.unmanaged} sims={sims} label="Not Onomondo-managed"
          hint="different carrier / no SIM reported — quota is managed outside the Portal"
        />
        <QuotaBucket
          cls="text-muted" things={buckets.unknown} sims={sims} label="Unknown"
          hint={copy.unknownHint}
        />
      </div>
      {needsAck && (
        <label className="flex items-start gap-2 text-xs text-warn">
          <input type="checkbox" checked={ack} onChange={(e) => onAck(e.target.checked)} className="mt-0.5" />
          <span>
            I understand
            {buckets.blocked.length > 0 &&
              ` ${buckets.blocked.length} device${buckets.blocked.length === 1 ? '' : 's'} cannot download at all (cap hit)`}
            {buckets.blocked.length > 0 && buckets.insufficient.length > 0 && ';'}
            {buckets.insufficient.length > 0 &&
              ` ${buckets.insufficient.length} device${buckets.insufficient.length === 1 ? '' : 's'} lack${buckets.insufficient.length === 1 ? 's' : ''} quota for this download`}
            {' '}— raise the SIM caps, or deploy anyway.
          </span>
        </label>
      )}
      {raiseFor && (
        <SimTopUpDialog
          target={{ mode: 'single', thing: raiseFor.thing, sim: raiseFor.sim }}
          onClose={() => setRaiseFor(null)}
        />
      )}
    </div>
  );
}

// A quota bucket row: expandable thing list with remaining-quota context and an
// inline "Raise…" action where a cap change would actually help.
function QuotaBucket({
  label, things, sims, cls, hint, onRaise,
}: {
  label: string;
  things: string[];
  sims: Record<string, DeviceSimStatus> | undefined;
  cls: string;
  hint: string;
  onRaise?: (thing: string) => void;
}) {
  if (things.length === 0) return null;
  return (
    <details className="min-w-0">
      <summary className={`cursor-pointer text-xs font-medium ${cls}`}>
        {label}: {things.length}
      </summary>
      <p className="text-[11px] text-muted mt-0.5 max-w-sm">{hint}</p>
      <ul className="mt-1 space-y-0.5">
        {things.map((t) => {
          const q = sims?.[t]?.sim?.quota;
          return (
            <li key={t} className="flex items-baseline gap-2">
              <Link
                to={`/devices/${encodeURIComponent(t)}`}
                className="font-mono text-[11px] hover:text-accent hover:underline"
              >
                {t}
              </Link>
              {q && (
                <span className="text-[11px] text-muted tabular-nums">
                  {fmtBytes(q.remainingBytes)} left of {fmtBytes(q.totalBytes)}
                </span>
              )}
              {onRaise && sims?.[t]?.sim && (
                <button
                  type="button"
                  onClick={() => onRaise(t)}
                  className="text-[11px] text-accent hover:underline"
                >
                  Raise…
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

// Map a deploy failure to a friendly operator message. The backend surfaces:
//   400 — bad combination / oversize doc
//   404 — artifact or content profile not found
//   409 — capability gate: device not release-capable, or (DE-186) force
//         requested but the device can't apply immediately. Both backend
//         messages are self-contained — pass them through.
//   502 — IoT CreateJob dispatch error (dispatchErrorMessage)
// ApiError carries the backend's `message`/`error` body; we prefix it with context.
function describeDeployError(reason: unknown): string {
  if (reason instanceof ApiError) {
    const msg = reason.message || 'deploy failed';
    switch (reason.status) {
      case 409:
        return msg;
      case 502:
        return `Dispatch failed: ${msg}`;
      case 404:
        return `Not found: ${msg}`;
      case 400:
        return `Rejected: ${msg}`;
      default:
        return msg;
    }
  }
  return (reason as Error)?.message ?? 'deploy failed';
}
